/**
 * A chemical formula or equation as plain text — CaCO₃ + 2 HCl → CaCl₂ + H₂O
 * + CO₂ — set with real Unicode subscripts rather than markup.
 *
 * The bench's equation used to go through KaTeX's `\ce{}`, the way the shelf
 * labels still do (see ChemFormula). That is fine where it renders into
 * ordinary page flow, but the reaction card lives inside an SVG
 * `<foreignObject>` — injected markup, a foreign namespace, and a web font
 * that is laid out before it is painted, all at once — and there the formulas
 * came out blank while the " + " and " → " between them still showed. Text
 * characters have none of those failure modes, wrap on their own, and inherit
 * the card's own typeface instead of dropping a serif formula into a sans
 * card.
 *
 * The input is the same mhchem-flavoured string the prompt asks the model for:
 * plain digits for subscripts, `->` for the arrow, a trailing `v`/`^` for a
 * precipitate or an escaping gas.
 */

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';
const SUPERSCRIPT = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '+': '⁺', '-': '⁻' } as const;

/** A digit lowers only when there is an atom or a closed group for it to
 *  belong to; one that opens a term is a coefficient (the 2 in "2 HCl"). */
function bindsToWhatCameBefore(prev: string): boolean {
  return prev !== '' && (/[A-Za-z)\]]/.test(prev) || SUBSCRIPT_DIGITS.includes(prev));
}

export function formatChemistry(source: string): string {
  let out = '';

  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    const next = source[i + 1] ?? '';
    const prev = out[out.length - 1] ?? '';

    if (ch === '-' && next === '>') {
      out += '→';
      i += 1;
      continue;
    }

    if (ch >= '0' && ch <= '9') {
      out += bindsToWhatCameBefore(prev) ? SUBSCRIPT_DIGITS[Number(ch)] : ch;
      continue;
    }

    if (ch === '^') {
      // mhchem spells a charge as "^2+" and an escaping gas as a bare "^"
      // after the formula. The two are told apart by what follows.
      const charge = /^[0-9]*[+-]?/.exec(source.slice(i + 1))?.[0] ?? '';
      if (charge) {
        out += [...charge].map(c => SUPERSCRIPT[c as keyof typeof SUPERSCRIPT] ?? c).join('');
        i += charge.length;
      } else {
        out = out.trimEnd() + '↑';
      }
      continue;
    }

    // A lone lowercase v is mhchem's precipitate arrow — never an element, so
    // there is nothing to confuse it with. Both arrows sit against the
    // formula they belong to rather than floating a space away.
    if (ch === 'v' && (prev === '' || prev === ' ') && (next === '' || next === ' ')) {
      out = out.trimEnd() + '↓';
      continue;
    }

    // A dot between two formulas is a hydrate/adduct separator (HgO.Hg(NH2)I),
    // not a full stop.
    if (ch === '.' && /[A-Za-z0-9)\]]/.test(prev) && /[A-Za-z(]/.test(next)) {
      out += '·';
      continue;
    }

    out += ch;
  }

  return out;
}
